/**
 * Motion system — one source for timing, easing and springs so every screen
 * moves the same way: crisp, physical, never in the way (Oct 2026 overhaul).
 *
 * Two engines:
 *   - CSS transitions (web): hover / press / focus / color / border changes.
 *     Cheap, runs on the compositor, no JS per frame. Use `webTransition()`.
 *   - Animated (all platforms): entrances, exits, springs, number tweens.
 *     Use `spring()` / `timing()`, which pick the native driver only off web.
 *
 * Every animation respects the OS "reduce motion" setting: durations collapse
 * to ~0 and springs become instant (`useReducedMotion`, `prefersReducedMotion`).
 */
import { useEffect, useState } from 'react';
import { Animated, Easing, Platform, AccessibilityInfo } from 'react-native';

export const IS_WEB = Platform.OS === 'web';

/** Milliseconds. "fast" is the default for feedback; "base" for movement. */
export const DURATION = {
    instant: 90,
    fast: 140,
    base: 200,
    slow: 280,
    page: 360,
};

/** CSS easing curves (web transitions). */
export const CSS_EASE = {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',        // most UI movement
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',          // entrances (expo-out feel)
    in: 'cubic-bezier(0.4, 0, 1, 1)',              // exits
    spring: 'cubic-bezier(0.34, 1.4, 0.64, 1)',    // small overshoot for press release / pops
};

/** Animated easings matching CSS_EASE. */
export const EASE = {
    standard: Easing.bezier(0.2, 0, 0, 1),
    out: Easing.bezier(0.16, 1, 0.3, 1),
    in: Easing.bezier(0.4, 0, 1, 1),
};

/** Spring presets for Animated.spring (stiffness/damping/mass). */
export const SPRING = {
    snappy: { stiffness: 520, damping: 34, mass: 1 },   // presses, toggles, pills
    smooth: { stiffness: 260, damping: 28, mass: 1 },   // panels, sheets
    pop: { stiffness: 420, damping: 18, mass: 0.9 },    // little celebratory overshoot
};

// ── Reduced motion ───────────────────────────────────────────────────────────
let _reduced = false;
const _listeners = new Set();

function _setReduced(v) {
    if (_reduced === v) return;
    _reduced = v;
    _listeners.forEach((fn) => fn(v));
}

if (IS_WEB && typeof window !== 'undefined' && window.matchMedia) {
    try {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        _reduced = mq.matches;
        const onChange = (e) => _setReduced(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange);
    } catch { /* no matchMedia */ }
} else if (!IS_WEB) {
    AccessibilityInfo.isReduceMotionEnabled?.().then((v) => _setReduced(!!v)).catch(() => {});
    AccessibilityInfo.addEventListener?.('reduceMotionChanged', (v) => _setReduced(!!v));
}

export function prefersReducedMotion() {
    return _reduced;
}

export function useReducedMotion() {
    const [reduced, setReduced] = useState(_reduced);
    useEffect(() => {
        _listeners.add(setReduced);
        setReduced(_reduced);
        return () => { _listeners.delete(setReduced); };
    }, []);
    return reduced;
}

// ── Animated helpers ─────────────────────────────────────────────────────────
// The native driver can't animate layout/color props and doesn't exist on web.
const nativeDriver = (useNative) => !IS_WEB && useNative !== false;

/** Animated.spring with a preset; instant under reduced motion. */
export function spring(value, toValue, preset = 'snappy', { useNative = true, ...rest } = {}) {
    if (_reduced) return Animated.timing(value, { toValue, duration: 0, useNativeDriver: nativeDriver(useNative) });
    const cfg = typeof preset === 'string' ? SPRING[preset] : preset;
    return Animated.spring(value, { toValue, ...cfg, useNativeDriver: nativeDriver(useNative), ...rest });
}

/** Animated.timing with a named duration/easing; instant under reduced motion. */
export function timing(value, toValue, { duration = 'base', easing = 'standard', useNative = true, delay = 0 } = {}) {
    const ms = typeof duration === 'number' ? duration : DURATION[duration];
    return Animated.timing(value, {
        toValue,
        duration: _reduced ? 0 : ms,
        delay: _reduced ? 0 : delay,
        easing: typeof easing === 'string' ? EASE[easing] : easing,
        useNativeDriver: nativeDriver(useNative),
    });
}

// ── CSS transition helper (web only) ─────────────────────────────────────────
/**
 * Style props that make react-native-web transition the given properties.
 * Off web returns {} (native gets Animated instead).
 *   webTransition(['transform', 'background-color'], 'fast')
 */
export function webTransition(properties = ['transform', 'opacity'], duration = 'fast', ease = 'standard') {
    if (!IS_WEB) return {};
    const ms = typeof duration === 'number' ? duration : DURATION[duration];
    return {
        transitionProperty: Array.isArray(properties) ? properties.join(', ') : properties,
        transitionDuration: `${ms}ms`,
        transitionTimingFunction: CSS_EASE[ease] || ease,
    };
}

/** Stagger delay for the nth item of a list entrance, capped so long lists don't drag. */
export function staggerDelay(index, step = 35, max = 280) {
    if (_reduced) return 0;
    return Math.min(Math.max(0, index) * step, max);
}
